import { useState } from "react";
import { Check, ArrowRight, ArrowLeft, Loader2, Upload, FileText, X, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "@/i18n/LanguageContext";
import { ALL_CATS } from "@/i18n/slugs";
import { FAMILIES } from "@/i18n/taxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOTAL_STEPS = 6;
const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const catByKeyLabel = (key, lang) => ALL_CATS.find((c) => c.key === key)?.[lang].name || key;

export default function MultiStepForm({ initialCategory = "", initialCity = "" }) {
  const { lang, T } = useLang();
  const F = T.form;
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [formData, setFormData] = useState({
    project_type: initialCategory || "",
    project_description: "",
    budget: "",
    timeline: "",
    start_date: "",
    postal_code: "",
    country: "BE",
    city: initialCity || "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    preferred_contact_method: "both",
    preferred_contact_hours: "",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [catSearch, setCatSearch] = useState("");
  const [openFamily, setOpenFamily] = useState(() =>
    initialCategory ? (ALL_CATS.find((c) => c.key === initialCategory)?.family || null) : null
  );

  const filteredCats = catSearch.trim().length >= 2
    ? ALL_CATS.filter((c) => norm(c[lang].name).includes(norm(catSearch.trim())) || norm(c[lang].nav).includes(norm(catSearch.trim())))
    : null;

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.project_type !== "";
      case 2:
        return formData.project_description.trim().length > 10;
      case 3:
        return formData.budget !== "" && formData.timeline !== "";
      case 4:
        return formData.postal_code !== "" && formData.city !== "";
      case 5:
        return (
          formData.first_name !== "" &&
          formData.last_name !== "" &&
          formData.email !== "" &&
          formData.phone !== ""
        );
      case 6:
        return emailCode.length === 6;
      default:
        return false;
    }
  };

  const sendVerificationCode = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads/send-verification`, { email: formData.email, lang });
      toast.success(F.toastCodeSent(formData.email));
      setCurrentStep(6);
    } catch (error) {
      if (error.response?.status === 429) {
        setCurrentStep(6);
        toast.info(F.toastCodeAlready);
      } else {
        toast.error(error.response?.data?.detail || F.toastCodeFail);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendCode = async () => {
    try {
      await axios.post(`${API}/leads/send-verification`, { email: formData.email, lang });
      toast.success(F.toastNewCode);
    } catch (error) {
      toast.error(error.response?.data?.detail || F.toastResendFail);
    }
  };

  const verifyAndSubmit = async () => {
    setIsSubmitting(true);
    try {
      const verifyRes = await axios.post(`${API}/leads/verify-email`, {
        email: formData.email,
        code: emailCode,
      });
      await handleSubmit(verifyRes.data.verification_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || F.toastWrongCode);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (verificationToken) => {
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...formData, verification_token: verificationToken, lang });
      const leadId = res.data.id;

      // Upload files if any
      if (selectedFiles.length > 0) {
        const fileData = new FormData();
        selectedFiles.forEach((file) => fileData.append("files", file));
        await axios.post(`${API}/leads/${leadId}/upload`, fileData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      setIsSuccess(true);
      toast.success(F.toastSuccess);
      setTimeout(() => {
        document.querySelector('[data-testid="form-success"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error(F.toastError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 5) {
      sendVerificationCode();
    } else if (currentStep === 6) {
      verifyAndSubmit();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-12 px-6" data-testid="form-success">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">
          {F.successTitle}
        </h3>
        <p className="text-slate-600 mb-6">
          {F.successText}
        </p>
        <Button
          onClick={() => {
            setIsSuccess(false);
            setCurrentStep(1);
            setEmailCode("");
            setSelectedFiles([]);
            setFormData({
              project_type: initialCategory || "",
              project_description: "",
              budget: "",
              timeline: "",
              postal_code: "",
              city: initialCity || "",
              first_name: "",
              last_name: "",
              email: "",
              phone: "",
            });
          }}
          variant="outline"
          data-testid="new-request-button"
        >
          {F.newRequest}
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="multi-step-form">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3, 4, 5, 6].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`step-indicator ${
                  step < currentStep
                    ? "completed"
                    : step === currentStep
                    ? "active"
                    : "pending"
                }`}
                data-testid={`step-indicator-${step}`}
              >
                {step < currentStep ? <Check className="w-5 h-5" /> : step}
              </div>
              {step < 6 && (
                <div
                  className={`hidden sm:block w-12 lg:w-20 h-1 mx-1 rounded ${
                    step < currentStep ? "bg-green-500" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-500 text-center">
          {F.stepOf(currentStep, TOTAL_STEPS)}
        </p>
      </div>

      {/* Step Content */}
      <div className="min-h-[300px]">
        {/* Step 1: Type of Work */}
        {currentStep === 1 && (
          <div className="animate-fade-in" data-testid="step-1">
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
              {F.s1Title}
            </h3>
            <Input
              type="text"
              value={catSearch}
              onChange={(e) => setCatSearch(e.target.value)}
              placeholder={lang === "nl" ? "Zoek een type werk (bv. dak, keuken, zonnepanelen...)" : "Rechercher un type de travaux (ex : toiture, cuisine, panneaux solaires...)"}
              className="mb-4"
              data-testid="category-search-input"
            />
            {formData.project_type && (
              <div className="mb-4 flex items-center gap-2 text-sm" data-testid="selected-category-chip">
                <span className="px-3 py-1.5 bg-orange-100 text-orange-700 font-semibold rounded-full">
                  {catByKeyLabel(formData.project_type, lang)}
                </span>
                <Check className="w-4 h-4 text-green-500" />
              </div>
            )}
            <div className="max-h-[340px] overflow-y-auto pr-1 space-y-2">
              {filteredCats ? (
                filteredCats.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center" data-testid="category-search-empty">
                    {lang === "nl" ? "Geen resultaat. Probeer een ander trefwoord." : "Aucun résultat. Essayez un autre mot-clé."}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {filteredCats.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => updateField("project_type", c.key)}
                        className={`text-left px-4 py-3 border rounded-xl text-sm font-medium transition-all ${
                          formData.project_type === c.key
                            ? "border-orange-500 bg-orange-50 text-orange-700"
                            : "border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}
                        data-testid={`cat-option-${c.key}`}
                      >
                        {c[lang].name}
                      </button>
                    ))}
                  </div>
                )
              ) : (
                FAMILIES.map((f) => {
                  const cats = ALL_CATS.filter((c) => c.family === f.key);
                  const isOpen = openFamily === f.key;
                  return (
                    <div key={f.key} className="border border-slate-200 rounded-xl overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenFamily(isOpen ? null : f.key)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                        data-testid={`family-toggle-${f.key}`}
                      >
                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                          <span>{f.icon}</span> {lang === "nl" ? f.nl : f.fr}
                        </span>
                        <span className="text-xs text-slate-400">{cats.length}</span>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {cats.map((c) => (
                            <button
                              key={c.key}
                              type="button"
                              onClick={() => updateField("project_type", c.key)}
                              className={`text-left px-3 py-2 border rounded-lg text-sm transition-all ${
                                formData.project_type === c.key
                                  ? "border-orange-500 bg-orange-50 text-orange-700 font-semibold"
                                  : "border-slate-200 hover:border-slate-300 text-slate-700"
                              }`}
                              data-testid={`cat-option-${c.key}`}
                            >
                              {c[lang].name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Step 2: Description */}
        {currentStep === 2 && (
          <div className="animate-fade-in" data-testid="step-2">
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">
              {F.s2Title}
            </h3>
            <Textarea
              placeholder={F.s2Placeholder}
              value={formData.project_description}
              onChange={(e) => updateField("project_description", e.target.value)}
              className="min-h-[150px] resize-none"
              data-testid="project-description-input"
            />
            <p className="text-sm text-slate-500 mt-2">
              {F.s2Min(formData.project_description.length)}
            </p>
          </div>
        )}

        {/* Step 3: Budget & Timeline */}
        {currentStep === 3 && (
          <div className="animate-fade-in space-y-8" data-testid="step-3">
            <div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                {F.s3Budget}
              </h3>
              <RadioGroup
                value={formData.budget}
                onValueChange={(value) => updateField("budget", value)}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {F.budgets.map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={`budget-${option.value}`}
                    className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                      formData.budget === option.value
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <RadioGroupItem value={option.value} id={`budget-${option.value}`} className="sr-only" />
                    <span className="font-medium text-slate-900">{option.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                {F.s3When}
              </h3>
              <RadioGroup
                value={formData.timeline}
                onValueChange={(value) => updateField("timeline", value)}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {F.timelines.map((option) => (
                  <Label
                    key={option.value}
                    htmlFor={`timeline-${option.value}`}
                    className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                      formData.timeline === option.value
                        ? "border-orange-500 bg-orange-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <RadioGroupItem value={option.value} id={`timeline-${option.value}`} className="sr-only" />
                    <span className="font-medium text-slate-900">{option.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                {F.s3Date}
              </h3>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => updateField("start_date", e.target.value)}
                className="max-w-xs"
                min={new Date().toISOString().split("T")[0]}
                data-testid="start-date-input"
              />
              <p className="text-sm text-slate-500 mt-2">{F.s3DateHint}</p>
            </div>
          </div>
        )}

        {/* Step 4: Location */}
        {currentStep === 4 && (
          <div className="animate-fade-in" data-testid="step-4">
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">
              {F.s4Title}
            </h3>
            <div className="mb-4">
              <Label className="text-slate-700 mb-2 block">{lang === "nl" ? "Land" : "Pays"}</Label>
              <div className="flex flex-wrap gap-2" data-testid="country-select">
                {[["BE", "🇧🇪", "Belgique", "België"], ["LU", "🇱🇺", "Luxembourg", "Luxemburg"], ["FR", "🇫🇷", "France", "Frankrijk"]].map(([code, flag, fr, nl]) => (
                  <button key={code} type="button" onClick={() => updateField("country", code)}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${formData.country === code ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"}`}
                    data-testid={`country-${code}`}>
                    {flag} {lang === "nl" ? nl : fr}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal_code" className="text-slate-700 mb-2 block">
                  {F.postal}
                </Label>
                <Input
                  id="postal_code"
                  placeholder={formData.country === "FR" ? "59000" : formData.country === "LU" ? "L-1234" : "1000"}
                  value={formData.postal_code}
                  onChange={(e) => updateField("postal_code", e.target.value)}
                  data-testid="postal-code-input"
                />
              </div>
              <div>
                <Label htmlFor="city" className="text-slate-700 mb-2 block">
                  {F.city}
                </Label>
                <Input
                  id="city"
                  placeholder={F.cityPlaceholder}
                  value={formData.city}
                  onChange={(e) => updateField("city", e.target.value)}
                  data-testid="city-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Contact Info */}
        {currentStep === 5 && (
          <div className="animate-fade-in" data-testid="step-5">
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">
              {F.s5Title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-slate-700 mb-2 block">
                  {F.firstName}
                </Label>
                <Input
                  id="first_name"
                  placeholder="Jean"
                  value={formData.first_name}
                  onChange={(e) => updateField("first_name", e.target.value)}
                  data-testid="first-name-input"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-slate-700 mb-2 block">
                  {F.lastName}
                </Label>
                <Input
                  id="last_name"
                  placeholder="Dupont"
                  value={formData.last_name}
                  onChange={(e) => updateField("last_name", e.target.value)}
                  data-testid="last-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700 mb-2 block">
                  {F.email}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="jean.dupont@email.com"
                  value={formData.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-700 mb-2 block">
                  {F.phone}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+32 470 123 456"
                  value={formData.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Contact preferences */}
            <div className="mt-6 pt-6 border-t border-slate-200 space-y-5">
              <div>
                <Label className="text-slate-700 mb-3 block font-medium">
                  {F.contactMethod}
                </Label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: "phone", label: F.byPhone },
                    { value: "email", label: F.byEmail },
                    { value: "both", label: F.both }
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => updateField("preferred_contact_method", opt.value)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                        formData.preferred_contact_method === opt.value
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"
                      }`}
                      data-testid={`contact-method-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-700 mb-3 block font-medium">
                  {F.contactHours}
                </Label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: "matin", label: F.morning },
                    { value: "apres-midi", label: F.afternoon },
                    { value: "soiree", label: F.evening },
                    { value: "flexible", label: F.flexible }
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => updateField("preferred_contact_hours", opt.value)}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                        formData.preferred_contact_hours === opt.value
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"
                      }`}
                      data-testid={`contact-hours-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <Label className="text-slate-700 mb-3 block font-medium">
                {F.docs}
              </Label>
              <p className="text-sm text-slate-500 mb-3">
                {F.docsHint}
              </p>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-orange-300 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.xlsx,.xls"
                  multiple
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files);
                    setSelectedFiles(prev => [...prev, ...newFiles]);
                    e.target.value = "";
                  }}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">
                    <span className="text-orange-500 font-medium">{F.clickAdd}</span> {F.files}
                  </p>
                </label>
              </div>
              {selectedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`file-${idx}`}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-orange-500" />
                        <span className="text-sm text-slate-700">{file.name}</span>
                        <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(0)} KB)</span>
                      </div>
                      <button type="button" onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))} className="p-1 hover:bg-slate-200 rounded">
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {currentStep === 6 && (
          <div className="animate-fade-in text-center" data-testid="step-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              {F.s6Title}
            </h3>
            <p className="text-slate-600 mb-6">
              {F.s6Text1} <strong>{formData.email}</strong>.<br />
              {F.s6Text2}
            </p>
            <div className="max-w-xs mx-auto">
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                className="text-center text-2xl tracking-[0.5em]"
                maxLength={6}
                data-testid="lead-verify-code-input"
              />
            </div>
            <button
              type="button"
              onClick={resendCode}
              className="mt-4 text-sm text-orange-500 hover:underline"
              data-testid="lead-verify-resend-button"
            >
              {F.resend}
            </button>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="gap-2"
          data-testid="prev-step-button"
        >
          <ArrowLeft className="w-4 h-4" />
          {F.back}
        </Button>

        <Button
          onClick={nextStep}
          disabled={!canProceed() || isSubmitting}
          className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
          data-testid="next-step-button"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {F.sending}
            </>
          ) : currentStep === 6 ? (
            F.confirm
          ) : currentStep === 5 ? (
            <>
              {F.verifyEmail}
              <ArrowRight className="w-4 h-4" />
            </>
          ) : (
            <>
              {F.next}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
